import { useMemo } from "react";
import { confirmDialog } from "../components/ConfirmDialog";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Pencil, Plus, Trash2, Eye, EyeOff, Sparkles } from "lucide-react";
import { categoryLabel } from "../lib/articleCategories";

export default function AdminArticlesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["admin-articles"],
    queryFn: async () => (await api.get("/admin/articles")).data,
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/articles/${id}`)).data,
    onSuccess: () => {
      toast.success("Article supprimé");
      qc.invalidateQueries({ queryKey: ["admin-articles"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const startCreate = () => navigate("/admin/articles/nouveau");
  const startEdit = (a) => navigate(`/admin/articles/${a.id}/modifier`);

  const sorted = useMemo(
    () => [...articles].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
    [articles],
  );

  return (
    <div className="mx-auto max-w-7xl px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2">Admin</div>
          <h1 className="font-display font-black text-3xl">Analyses</h1>
        </div>
        <Button
          onClick={startCreate}
          className="bg-emerald-500 text-black hover:bg-emerald-300"
          data-testid="admin-articles-new"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nouvel article
        </Button>
      </div>

      {isLoading ? (
        <div className="text-zinc-500">Chargement…</div>
      ) : sorted.length === 0 ? (
        <Card className="p-12 text-center bg-card border-white/5">
          <p className="text-zinc-500">Aucun article pour l'instant.</p>
          <Button className="mt-4 bg-emerald-500 text-black hover:bg-emerald-300" onClick={startCreate}>
            <Plus className="w-4 h-4 mr-1.5" /> Créer le premier article
          </Button>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="admin-articles-list">
          {sorted.map((a) => (
            <Card key={a.id} className="p-4 bg-card border-white/5 flex items-center gap-4" data-testid={`admin-article-row-${a.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-medium truncate">{a.title}</h3>
                  {a.tier_required !== "free" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] uppercase tracking-[0.15em]">
                      <Sparkles className="w-3 h-3" /> {a.tier_required}
                    </span>
                  )}
                  {!a.published && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-500/10 text-zinc-400 text-[10px] uppercase tracking-[0.15em]">
                      <EyeOff className="w-3 h-3" /> Brouillon
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {categoryLabel(a.category)} · /articles/{a.slug} · {a.tier_required !== "free"
                    ? (a.has_preview_break ? "✂ coupure manuelle" : `aperçu ${a.preview_char_limit} car.`)
                    : `aperçu ${a.preview_char_limit} car.`}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => window.open(`/articles/${a.slug}`, "_blank")} data-testid={`article-view-${a.slug}`}>
                <Eye className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => startEdit(a)} data-testid={`article-edit-${a.slug}`}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-rose-400 hover:bg-rose-500/10"
                onClick={async () => {
                  if (await confirmDialog({ title: `Supprimer « ${a.title} » ?`, description: "L'analyse disparaît du site. Action définitive." })) deleteMut.mutate(a.id);
                }}
                data-testid={`article-delete-${a.slug}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
